import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { correctAnswerIssues, duplicateValueIssues, feedbackIssues, minimumOptions, optionIssues, requireOptions, trimmed, valuesOf } from './_checks.js';
import { draftFields, Option, questionFields } from './_shared.js';
import { questionMayBeNull } from './templatePolicy.js';
import { activityIssue, asList } from './types.js';

/**
 * A multiple choice question with several answers. `correctAnswerMultiple` holds option
 * values*; each is checked, because one typo in the list silently makes a correct
 * selection ungradeable while the other entries keep working.
 */
export const multiChoiceMultiResponse: ActivityDefinition = {
  type: 'multi_choice_multi_response',
  summary: 'A multiple choice question where several options are correct.',
  setup: z.looseObject({
    ...draftFields,
    ...questionFields,
    options: z.array(Option).optional(),
    correctAnswerMultiple: z.array(z.string()).optional(),
  }),
  check: (setup, ctx) => {
    const options = asList(setup.options);
    const issues = [
      ...requireOptions('multi_choice_multi_response', options, ctx),
      ...minimumOptions('multi_choice_multi_response', options, ctx),
      ...feedbackIssues('multi_choice_multi_response', setup.feedback, ctx),
      ...optionIssues('multi_choice_multi_response', options, ctx),
      ...duplicateValueIssues('multi_choice_multi_response', options, ctx),
    ];

    // "Pick all that apply" means nothing without the thing to apply it to. A null question is
    // judged per template by validateActivity; an absent one is a hole everywhere but a gameshow.
    if (setup.question === undefined && !questionMayBeNull(ctx.templateName)) {
      issues.push(activityIssue('multi_choice_multi_response')(
        'question',
        'No question is written; the player is asked to pick several answers to nothing.',
        [...ctx.path, 'setup', 'question'],
        ctx,
      ));
    }

    issues.push(...correctAnswerIssues({
      type: 'multi_choice_multi_response',
      field: 'correctAnswerMultiple',
      answers: asList(setup.correctAnswerMultiple).map(trimmed),
      values: valuesOf(options),
      ctx,
      indexed: true,
    }));

    return issues;
  },
};
